import { FilteredCongestionReportHeroProps, TopTasks } from 'components/CongestionReportsHero';
import { useCongestionDetails } from 'src/hooks/useCongestionDetails';
import React from 'react';
import { CongestionReportHeroView } from './_View';
import { ErrorMessage } from './_ErrorMessage';
import { useMapRings } from './hooks/useMapRings';

export type CongestionReportHeroProps = {
  topTasks: TopTasks;
} & Omit<FilteredCongestionReportHeroProps, 'tasks'>;

/**
 * This component should be used within a CongestionReportContainer.
 * The CongestionReportContainer will provide this component with the congestionReport.
 * getCongestionReport is added to retrieve the congestionReport incase the congestionReport is not provided.
 */
export const CongestionReportHero = ({ congestionReport, ...props }: CongestionReportHeroProps) => {
  const {
    isLoading,
    isError,
    congestionReport: data,
  } = useCongestionDetails(
    congestionReport,
    props.isLoading,
    props.isError,
    props.enableResolvedDateRange
  );

  // We request the rings separately to keep te load time as low as possible.
  // When the postal codes are not available, in the database we request them
  // from Argis and there service is very slow.
  // Once they are requested the back-end will save the rings in there database.
  // And the loading time will be significant shorter.
  const rings = useMapRings({ postalCodes: data?.affectedPostalCodes ?? '' });

  const isNotFound = !isLoading && !data?.id && !data?.name && !data?.locations;

  // When the congestion report is not found, we show a message that the report is not found.
  if (isNotFound || isError) {
    return (
      <ErrorMessage
        title={isError ? props.errorTitle?.toString() : props.notFoundTitle?.toString()}
        text={isError ? props.errorText?.toString() : props.errorText?.toString()}
      />
    );
  }

  return (
    <CongestionReportHeroView
      {...props}
      data={data}
      rings={rings}
      isLoading={Boolean(isLoading)}
      mainTitle={props.title?.toString() ?? ''}
    />
  );
};
